export const formatDateToISODate = (date: Date): string => {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

/**
 * Parse a date-only ISO string ("2026-06-01") as a LOCAL calendar day.
 *
 * DO NOT "simplify" this to `new Date(value)`. That parses a date-only string as
 * UTC midnight, so reading its LOCAL date parts back — which is what every
 * formatter here does, `dateToString` included — yields the PREVIOUS day in any
 * UTC-negative timezone: `2026-06-01` renders as 31/05/2026 for an operator in
 * New York (GOTCHAS `date-only-string-utc-offbyone`). Building from the parts
 * puts the date at local midnight, where the local parts are the ones the server
 * meant. It is invisible under the suite's UTC pin, so only a timezone-flipping
 * test catches a regression (`dates.test.ts`).
 *
 * A grid column whose CELL VALUE feeds `agDateColumnFilter` is the one place
 * that deliberately keeps UTC-midnight `Date`s instead — see
 * `utcDateFilterComparator` in `utils/grid.ts`, which compares UTC parts against
 * the filter's local ones. Format the label with this function; leave the value
 * alone.
 */
export const parseIsoDateOnly = (value: string | undefined): Date | undefined => {
    if (!value) {
        return undefined;
    }
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
};

/**
 * "January 2026" — the month a date-only (or date-and-time) ISO string falls in.
 *
 * For naming a MONTH to the operator, where a day would be noise or a lie: the
 * business fee is charged per whole month, so its effective date and the months
 * it leaves uncovered are month-granular facts (`manual-business-fee/FR-003`).
 * Parsed through `parseIsoDateOnly` for the timezone reason documented there.
 */
export const formatIsoMonth = (value: string | undefined): string => {
    const date = parseIsoDateOnly(value);
    if (!date) {
        return "";
    }
    return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};
